/* Iconos — SVG en línea, dibujados aquí y en ningún otro sitio.
 *
 * Dos reglas que los gobiernan:
 *
 * 1. NADA DE EMOJI en la interfaz. Un emoji no es un icono: lo dibuja el sistema operativo, así que
 *    cambia de forma y de color entre plataformas, no hereda `currentColor`, y los lectores de
 *    pantalla lo leen con su nombre Unicode («candado cerrado») en medio de una frase.
 *
 * 2. EN LÍNEA, no una fuente de iconos ni un sprite remoto. La CSP de producción bloquea cualquier
 *    petición a otro host, así que un icono servido de fuera simplemente no aparece — y falla en
 *    producción, donde no se está mirando.
 *
 * Heredan el color con `currentColor` y el tamaño con `1em`, para que basten el color y el tamaño
 * de letra del sitio donde se pongan. Van `aria-hidden`: el significado lo lleva el texto que
 * acompaña, nunca el icono solo.
 */
type Props = { className?: string }

/**
 * `grosor` existe porque el peso ÓPTICO no es el peso nominal: un trazo de 1,6 sobre un lienzo de
 * 16 unidades se ve firme en un aspa que cruza toda la caja y se desvanece en un signo estrecho.
 * Lo que manda es cómo se lee al TAMAÑO REAL (12-13 px junto a un ✓ y un ✗), no qué número dice el
 * atributo. Se ajusta por icono y se comprueba en una captura, no a ojo sobre el SVG ampliado.
 */
function Svg({ children, className, grosor = 1.6 }: Props & { children: React.ReactNode; grosor?: number }) {
  return (
    <svg
      className={className}
      width="1em"
      height="1em"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={grosor}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  )
}

/** Cumple. El diseño lo escribe como un ✓ en negrita 700; de ahí el trazo 2,2 y no 1,6. */
export function IconoSi({ className }: Props) {
  return (
    <Svg className={className} grosor={2.2}>
      <path d="M3 8.5 6.2 11.7 13 4.9" />
    </Svg>
  )
}

/** No cumple. Mismo peso que el ✓: los tres signos de criterio se leen como un juego. */
export function IconoNo({ className }: Props) {
  return (
    <Svg className={className} grosor={2.2}>
      <path d="M4 4l8 8M12 4l-8 8" />
    </Svg>
  )
}

/**
 * Advertencia sustantiva: no descarta ni respalda, pero hay que mirarlo antes de invertir horas
 * («se divide en 3 lotes», «su proveedor habitual concentra el 58%»). Va en oro y NO es lo mismo
 * que «sin dato» — colapsarlos perdía información en las dos direcciones.
 *
 * ⚠ Este es el icono que más fácil se pierde, y se perdió: con el palo a grosor 1,6 y el punto a
 * `r="0.5"` —medio punto sobre dieciséis unidades— a 12,5 px no quedaba nada en pantalla. En el
 * diseño el «!» es un glifo en NEGRITA 700 en oro, con la misma presencia que el ✓ y el ✗ que
 * tiene al lado, así que aquí lleva trazo 2,4 y punto de radio 1,3. Si se toca, se comprueba en
 * una CAPTURA al tamaño real junto a los otros dos: un aviso que se insinúa no avisa.
 */
export function IconoAviso({ className }: Props) {
  return (
    <Svg className={className} grosor={2.4}>
      <path d="M8 3.2v5.2" />
      <circle cx="8" cy="11.9" r="1.3" fill="currentColor" stroke="none" />
    </Svg>
  )
}

/**
 * No evaluable: no hay dato con qué decidir. Un guion, no una advertencia — no es una falta.
 *
 * Va un punto por debajo del ✓/✗/! (2,0 y no 2,2) porque debe pesar MENOS que ellos; pero no menos
 * que el 1,6 de partida, que a 12,5 px lo dejaba en un pelo. Quieto no es lo mismo que invisible.
 */
export function IconoSinDato({ className }: Props) {
  return (
    <Svg className={className} grosor={2}>
      <path d="M3.5 8h9" />
    </Svg>
  )
}

/**
 * Quitar de una lista (un tema, un departamento). Es la misma aspa que `IconoNo`, y se declara
 * aparte a propósito: ahí significa «no cumple» y aquí «bórrame esto». Dos significados con un
 * solo nombre acaban con alguien poniendo la de veredicto en un botón, en rojo, junto a algo que
 * la persona escribió.
 */
export function IconoQuitar({ className }: Props) {
  return (
    <Svg className={className}>
      <path d="M4.6 4.6l6.8 6.8M11.4 4.6l-6.8 6.8" />
    </Svg>
  )
}

/** Añadir a una lista. Acompaña siempre a un texto («+ agregar un tema»), nunca va solo. */
export function IconoMas({ className }: Props) {
  return (
    <Svg className={className}>
      <path d="M8 3.8v8.4M3.8 8h8.4" />
    </Svg>
  )
}

/**
 * Volver. El diseño escribe «← Volver a la ficha» con el carácter de flecha, y aquí va en SVG por
 * la misma razón que lo demás: `←` lo dibuja la fuente del sistema —cambia de peso y de alineación
 * vertical entre plataformas— y un lector de pantalla lo lee «flecha hacia la izquierda» delante de
 * «Volver a la ficha», que ya lo dice.
 */
export function IconoVolver({ className }: Props) {
  return (
    <Svg className={className}>
      <path d="M12.5 8h-9M6.6 4.1 2.7 8l3.9 3.9" />
    </Svg>
  )
}

/** Seguir. La otra mitad del par, para «Soltar el contexto y preguntar de todo SECOP». */
export function IconoSeguir({ className }: Props) {
  return (
    <Svg className={className}>
      <path d="M3.5 8h9M9.4 4.1 13.3 8l-3.9 3.9" />
    </Svg>
  )
}

/**
 * Enlace que SALE de la aplicación (la ficha oficial en SECOP). El diseño escribe «↗» y aquí va en
 * SVG por la regla de siempre: la flecha oblicua la dibuja la fuente del sistema, cambia de peso y
 * de alineación entre plataformas, y un lector de pantalla la lee «flecha noreste» en medio de la
 * frase.
 */
export function IconoExterno({ className }: Props) {
  return (
    <Svg className={className}>
      <path d="M6.2 9.8 12 4" />
      <path d="M7.4 4H12v4.6" />
      <path d="M11.4 9.6v2.2a1.2 1.2 0 0 1-1.2 1.2H4.2A1.2 1.2 0 0 1 3 11.8V5.8a1.2 1.2 0 0 1 1.2-1.2h2.2" />
    </Svg>
  )
}

/** Alcance de datos restringido. */
export function IconoCandado({ className }: Props) {
  return (
    <Svg className={className}>
      <rect x="3.2" y="7.2" width="9.6" height="6.4" rx="1.4" />
      <path d="M5.6 7.2V5.2a2.4 2.4 0 0 1 4.8 0v2" />
    </Svg>
  )
}

/** Menú plegado. Tres trazos, que es lo que el documento de móvil dibuja como «☰» — pero como SVG:
 *  un glifo suelto depende de la fuente del sistema y cambia de tamaño y de peso entre teléfonos. */
export function IconoMenu({ className }: Props) {
  return (
    <Svg className={className} grosor={1.8}>
      <path d="M2.5 4.5h11M2.5 8h11M2.5 11.5h11" />
    </Svg>
  )
}

/** Se despliega. El «▾» de la píldora de empresa, por el mismo motivo que el de arriba. */
export function IconoDesplegar({ className }: Props) {
  return (
    <Svg className={className} grosor={1.8}>
      <path d="M4 6.5l4 4 4-4" />
    </Svg>
  )
}
